import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from '@emburse/embark-core';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import CorpAllowancesHeader from './CorpAllowancesHeader';
import CorpAllowancesTable from './CorpAllowancesTable';
import CorpAllowancesDrawer from './CorpAllowancesDrawer';
import CorpAllowancesModal, { AllowanceModalTypes } from './CorpAllowancesModal';
import { getAllowances, deleteAllowance, createAllowance, updateAllowance } from '../../api/allowance';
import { AllowanceFilter, defaultPagination } from '../../interfaces/Allowance';
import { $t, useTranslation } from '../../hooks/useTranslation';
const getDefaultFilter = () => {
    const cachedPref = localStorage.getItem('afls');
    return Object.keys(AllowanceFilter).includes(cachedPref)
        ? parseInt(cachedPref, 10)
        : AllowanceFilter.InProgress;
};
function CorpAllowances({ BulkUploadLink }) {
    useTranslation();
    const [loading, setLoading] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [allowanceData, setAllowanceData] = useState([]);
    const [rowCount, setRowCount] = useState(null);
    const [page, setPage] = useState(defaultPagination.page);
    const [pageSize, setPageSize] = useState(defaultPagination.pageSize);
    const [sortModel, setSortModel] = useState(defaultPagination.sort);
    const [selectedRow, setSelectedRow] = useState(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState(getDefaultFilter());
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [invalidFields, setInvalidFields] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const handleError = useCallback((err) => {
        enqueueSnackbar(err.message, { variant: 'alert', AlertProps: { severity: 'error' } });
    }, [enqueueSnackbar]);
    useEffect(() => {
        setPage(1);
    }, [filter, search]);
    useEffect(() => {
        const pagination = {
            page,
            pageSize,
            sort: sortModel
        };
        const allowanceParams = {
            search,
            filter,
            pagination
        };
        setLoading(true);
        getAllowances(allowanceParams)
            .then((data) => {
            setRowCount(data.Count || 0);
            setAllowanceData([
                ...data.Results.map((row) => ({
                    ...row,
                    id: row.PK
                }))
            ]);
            setLoading(false);
        })
            .catch(handleError);
    }, [search, filter, page, pageSize, sortModel, handleError]);
    const sortChangeHandler = (sortArray) => {
        setSortModel(sortArray.length
            ? { field: sortArray[0].field, sort: sortArray[0].sort }
            : defaultPagination.sort);
    };
    const pageChangeHandler = (newPage, details, newPageSize) => {
        setPage(newPage + 1);
        setPageSize(newPageSize);
    };
    const searchChangeHandler = (val) => {
        setSearch(val);
    };
    const filterChangeHandler = (val) => {
        setFilter(val);
        localStorage.setItem('afls', val.toString());
    };
    const selectedRowHandler = (allowance) => {
        setSelectedRow(allowance);
    };
    const toggleDrawer = () => {
        setIsDrawerOpen((prevState) => !prevState);
    };
    const addAllowanceHandler = () => {
        setSelectedRow(null);
        toggleDrawer();
    };
    const updateAllowanceData = (allowance, PK) => {
        const updatedData = {
            ...allowance,
            PK,
            id: PK
        };
        setIsDrawerOpen(false);
        setAllowanceData((prevState) => [updatedData, ...prevState]);
        enqueueSnackbar($t('A new Allowance has been created!'), {
            variant: 'alert',
            AlertProps: { severity: 'success' }
        });
    };
    const editAllowance = (allowance) => {
        const updatedAllowances = allowanceData.map((item) => {
            if (item.PK === allowance.PK) {
                return { ...item, ...allowance };
            }
            return item;
        });
        setIsDrawerOpen(false);
        setAllowanceData(updatedAllowances);
        enqueueSnackbar($t('The Allowance has been updated'), {
            variant: 'alert',
            AlertProps: { severity: 'info' }
        });
    };
    const saveClickHandler = (allowance) => {
        setLoading(true);
        if (allowance.PK === undefined) {
            createAllowance(allowance)
                .then(({ Status, Data }) => {
                if (Status === 'success') {
                    updateAllowanceData(allowance, Data);
                }
                else {
                    setInvalidFields(Data);
                }
                setLoading(false);
            })
                .catch((err) => handleError(err));
        }
        else {
            updateAllowance(allowance)
                .then(({ Status, Data }) => {
                if (Status === 'success') {
                    editAllowance(allowance);
                }
                else {
                    setInvalidFields(Data);
                }
                setLoading(false);
            })
                .catch(handleError);
        }
    };
    const confirmModalHandler = () => {
        switch (modalType) {
            case AllowanceModalTypes.Delete:
                setLoading(true);
                deleteAllowance(selectedRow.PK)
                    .then(() => {
                    setAllowanceData((prevState) => prevState.filter((x) => x.PK !== selectedRow.PK));
                    setLoading(false);
                    if (isDrawerOpen) {
                        toggleDrawer();
                    }
                })
                    .catch(handleError);
                break;
            case AllowanceModalTypes.Cancel:
                toggleDrawer();
                break;
            default:
                break;
        }
    };
    const cancelClickHandler = (noConfirm) => {
        if (noConfirm) {
            toggleDrawer();
        }
        else {
            setModalType(AllowanceModalTypes.Cancel);
            setShowModal(true);
        }
    };
    const closeModalHandler = () => {
        setShowModal(false);
    };
    return (_jsxs(SingleColumnLayout, { children: [_jsx(CorpAllowancesModal, { open: showModal, modalType: modalType, onConfirm: confirmModalHandler, onClose: closeModalHandler }), _jsx(CorpAllowancesDrawer, { anchor: "right", open: isDrawerOpen, loading: loading, invalidFields: invalidFields, openUpdated: toggleDrawer, saveClickHandler: saveClickHandler, selectedAllowance: selectedRow, deleteClickHandler: () => {
                    setModalType(AllowanceModalTypes.Delete);
                    setShowModal(true);
                }, cancelClickHandler: cancelClickHandler }), _jsx(CorpAllowancesHeader, { onAddAllowance: addAllowanceHandler, bulkUploadLink: BulkUploadLink }), _jsx(CorpAllowancesTable, { loading: loading, rows: allowanceData, columns: [], rowCount: rowCount, onSortChange: sortChangeHandler, onPageChange: pageChangeHandler, onSearchChange: searchChangeHandler, onFilterChange: filterChangeHandler, onDelete: () => {
                    setModalType(AllowanceModalTypes.Delete);
                    setShowModal(true);
                }, onEdit: () => {
                    toggleDrawer();
                }, selectedRowHandler: selectedRowHandler, selectedPageSize: pageSize, selectedPage: page - 1 })] }));
}
export default CorpAllowances;
